import {
  useCallback,
  useEffect,
  useId,
  useImperativeHandle,
  useMemo,
  useRef,
  useState,
} from 'react';

import {
  type SelectPlacement,
  findEnabledIndex,
  findOptionByPrefix,
  isRepeatedCharacter,
  resolveSelectedOption,
} from './selectLogic';
import type { SelectAppearance } from './types/SelectAppearance';
import type { SelectOptionDataType } from './types/SelectOptionDataType';
import type { SelectProps } from './types/SelectProps';
import { useSelectKeyboard } from './useSelectKeyboard';

const TYPEAHEAD_RESET_MS = 600;

type SelectControllerOptions = Pick<
  SelectProps,
  | 'defaultMenuIsOpen'
  | 'defaultValue'
  | 'dropDownSize'
  | 'instanceId'
  | 'inputId'
  | 'isDisabled'
  | 'isReadOnly'
  | 'menuPlacement'
  | 'onBlur'
  | 'onChange'
  | 'options'
  | 'portalTarget'
  | 'ref'
  | 'rightAligned'
  | 'value'
> & {
  appearance: SelectAppearance;
};

export function useSelectController({
  appearance,
  defaultMenuIsOpen = false,
  defaultValue = null,
  dropDownSize = 'md',
  inputId,
  instanceId,
  isDisabled = false,
  isReadOnly = false,
  menuPlacement = 'auto',
  onBlur,
  onChange,
  options,
  portalTarget,
  ref,
  rightAligned = false,
  value,
}: SelectControllerOptions) {
  const generatedId = useId();
  const baseId = (inputId ?? instanceId ?? generatedId).replace(/[^\w-]/g, '');
  const listboxId = `${baseId}-listbox`;
  const getOptionId = useCallback(
    (index: number) => `${baseId}-option-${index}`,
    [baseId],
  );

  const [isOpen, setIsOpen] = useState(defaultMenuIsOpen);
  const [activeIndex, setActiveIndex] = useState(-1);
  const [placement, setPlacement] = useState<SelectPlacement>(
    menuPlacement === 'top' ? 'top' : 'bottom',
  );
  const [uncontrolledValue, setUncontrolledValue] =
    useState<SelectOptionDataType | null>(defaultValue);

  const containerRef = useRef<HTMLDivElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const optionRefs = useRef<(HTMLLIElement | null)[]>([]);
  const typeahead = useRef({ query: '', timer: 0 });

  const isControlled = value !== undefined;
  const isInteractive = !isDisabled && !isReadOnly;
  const isSmallSize = dropDownSize === 'sm';
  const selectedOption = isControlled
    ? resolveSelectedOption(value, options)
    : uncontrolledValue;
  const selectedIndex = useMemo(
    () =>
      selectedOption
        ? options.findIndex((option) => option.value === selectedOption.value)
        : -1,
    [options, selectedOption],
  );

  useImperativeHandle(ref, () => triggerRef.current as HTMLButtonElement, []);

  const closeMenu = useCallback(() => {
    setIsOpen(false);
    setActiveIndex(-1);
  }, []);
  const focusTrigger = useCallback(() => {
    triggerRef.current?.focus({ preventScroll: true });
  }, []);

  const resolvePlacement = useCallback((): SelectPlacement => {
    if (menuPlacement !== 'auto') return menuPlacement;
    const rect = triggerRef.current?.getBoundingClientRect();
    if (!rect) return 'bottom';

    const estimated = appearance.estimateMenuHeight(
      options.length,
      isSmallSize,
    );
    const spaceBelow = window.innerHeight - rect.bottom;
    return spaceBelow < estimated && rect.top > spaceBelow ? 'top' : 'bottom';
  }, [appearance, isSmallSize, menuPlacement, options.length]);

  const openMenu = useCallback(
    (initialIndex?: number) => {
      if (!isInteractive) return;
      setPlacement(resolvePlacement());
      setActiveIndex(
        initialIndex ??
          (selectedIndex >= 0 && !options[selectedIndex]?.isDisabled
            ? selectedIndex
            : findEnabledIndex(options, 0, 1)),
      );
      setIsOpen(true);
    },
    [isInteractive, options, resolvePlacement, selectedIndex],
  );

  const selectOption = useCallback(
    (index: number) => {
      const option = options[index];
      if (!option || option.isDisabled) return;
      if (!isControlled) setUncontrolledValue(option);
      onChange?.(option);
      closeMenu();
      focusTrigger();
    },
    [closeMenu, focusTrigger, isControlled, onChange, options],
  );

  const runTypeahead = useCallback(
    (character: string) => {
      window.clearTimeout(typeahead.current.timer);
      const query = (typeahead.current.query + character).toLowerCase();
      typeahead.current.query = query;
      typeahead.current.timer = window.setTimeout(() => {
        typeahead.current.query = '';
      }, TYPEAHEAD_RESET_MS);

      const current = isOpen ? activeIndex : selectedIndex;
      let match = findOptionByPrefix(
        options,
        query,
        query.length > 1 ? Math.max(current, 0) : current + 1,
      );
      if (match === -1 && isRepeatedCharacter(query)) {
        match = findOptionByPrefix(options, query[0], current + 1);
      }
      if (match === -1) return;
      if (isOpen) setActiveIndex(match);
      else openMenu(match);
    },
    [activeIndex, isOpen, openMenu, options, selectedIndex],
  );

  const handleKeyDown = useSelectKeyboard({
    activeIndex,
    closeMenu,
    focusTrigger,
    isInteractive,
    isOpen,
    openMenu,
    options,
    runTypeahead,
    selectOption,
    selectedIndex,
    setActiveIndex,
  });

  const handleBlur = useCallback(() => {
    if (isOpen) closeMenu();
    onBlur?.();
  }, [closeMenu, isOpen, onBlur]);

  const positionMenu = useCallback(
    (node: HTMLDivElement | null) => {
      menuRef.current = node;
      if (!node || !portalTarget) return;
      const rect = triggerRef.current?.getBoundingClientRect();
      if (!rect) return;

      node.style.position = 'absolute';
      node.style.left = `${rect.left + window.scrollX}px`;
      node.style.top = `${(placement === 'top' ? rect.top : rect.bottom) + window.scrollY}px`;
      node.style.width = `${rect.width}px`;
    },
    [placement, portalTarget],
  );

  useEffect(() => {
    if (!isOpen) return undefined;
    const handlePointerDown = (event: MouseEvent | TouchEvent) => {
      const target = event.target as Node;
      if (
        !containerRef.current?.contains(target) &&
        !menuRef.current?.contains(target)
      ) {
        closeMenu();
      }
    };
    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('touchstart', handlePointerDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('touchstart', handlePointerDown);
    };
  }, [closeMenu, isOpen]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const list = listRef.current;
    const item = optionRefs.current[activeIndex];
    if (!list || !item) return;
    const top = item.offsetTop;
    const bottom = top + item.offsetHeight;
    if (top < list.scrollTop) list.scrollTop = top;
    else if (bottom > list.scrollTop + list.clientHeight) {
      list.scrollTop = bottom - list.clientHeight;
    }
  }, [activeIndex, isOpen]);

  useEffect(() => {
    const timerRef = typeahead;
    return () => window.clearTimeout(timerRef.current.timer);
  }, []);

  return {
    activeIndex,
    closeMenu,
    containerRef,
    focusTrigger,
    getOptionId,
    handleBlur,
    handleKeyDown,
    isInteractive,
    isOpen,
    isSmallSize,
    listboxId,
    listRef,
    menuState: { isSmallSize, placement, rightAligned },
    openMenu,
    optionRefs,
    positionMenu,
    selectedIndex,
    selectedOption,
    selectOption,
    setActiveIndex,
    triggerRef,
  };
}
